// Copyright (c) 2020-2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import './AgentView.scss';
import Button from "../../../../nor/ui/components/button/Button";
import Icon from "../../../../nor/ui/components/icon/Icon";
import {
    AddIcon,
    TrashIcon,
    UndoIcon
} from "../../../../assets/icon";
import {UNDO_HISTORY_LENGTH} from "../../../../constants/common";
import {ChangeCallback, VoidCallback} from "../../../../nor/ts/interfaces/callbacks";
import FormAppClassName from "../../../../constants/FormAppClassName";
import AgentService, {
    AgentServiceDestructor,
    AgentServiceEvent
} from "../../../../services/AgentService";
import RepositoryEntry from "../../../../nor/ts/simpleRepository/types/RepositoryEntry";
import LogService from "../../../../nor/ts/LogService";
import Loader from "../../../../nor/ui/components/loader/Loader";
import AppRoute from "../../../../constants/AppRoute";
import RouteService from "../../../../nor/ui/services/RouteService";
import AgentPoolModel from "../../../../nor/pipeline/types/AgentPoolModel";
import AgentPoolDTO from "../../../../nor/pipeline/dto/AgentPoolDTO";
import TextField from "../../../../nor/ui/components/fields/text/TextField";
import FormLoginModal from "../../../../modals/FormLoginModal";
import { ObserverCallback } from "../../../../nor/ts/Observer";
import AgentAccountDTO from "../../../../nor/pipeline/dto/AgentAccountDTO";
import { map } from "../../../../nor/ts/modules/lodash";
import MemberList from "../../../../nor/ui/components/memberList/MemberList";
import RepositoryMember from "../../../../nor/ts/simpleRepository/types/RepositoryMember";

const LOG = LogService.createLogger('AgentView');

export const AGENT_SAVE_TIMEOUT = 200;

export enum AgentViewComponentState {

    UNLOADED,
    LOADING,
    LOADED,
    SAVING,
    LOAD_ERROR,
    SAVE_ERROR

}

export interface AgentViewState {

    readonly id               : string | undefined;
    readonly version          : number;
    readonly model            : AgentPoolModel | undefined;
    readonly dto              : AgentPoolDTO | undefined;
    readonly modelHistory     : AgentPoolModel[];
    readonly componentState   : AgentViewComponentState;
    readonly account          : AgentAccountDTO | undefined;

}

export interface AgentViewProps {

    readonly id         : string;
    readonly hasSession : boolean;

}

export class AgentView extends React.Component<AgentViewProps, AgentViewState> {

    private readonly _changeModelCallback      : ChangeCallback<AgentPoolModel>;
    private readonly _changeModelNameCallback  : ChangeCallback<string|undefined>;
    private readonly _undoModelCallback        : VoidCallback;
    private readonly _deleteCallback           : VoidCallback;
    private readonly _addNewCallback           : VoidCallback;
    private readonly _createdAccountCallback   : ObserverCallback<AgentServiceEvent, [AgentAccountDTO]>;

    private _componentState  : AgentViewComponentState;
    private _saveTimeout     : any | undefined;
    private _createdListener : AgentServiceDestructor | undefined;

    public constructor (props: AgentViewProps) {

        super(props);

        this._componentState = AgentViewComponentState.UNLOADED;

        this._saveTimeout = undefined;

        this.state = {
            id: undefined,
            model: undefined,
            dto: undefined,
            version: 0,
            modelHistory: [],
            componentState: this._componentState,
            account: undefined
        };

        this._changeModelCallback     = this._onChangeModel.bind(this);
        this._undoModelCallback       = this._onUndoModel.bind(this);
        this._deleteCallback          = this._onDelete.bind(this);
        this._changeModelNameCallback = (value : string | undefined) => this._changeModelCallback({name: value ?? ''});
        this._addNewCallback          = this._addNew.bind(this);
        this._createdAccountCallback  = this._onCreated.bind(this);

    }

    public componentDidMount (): void {

        this._loadBackendItem();

        this._createdListener = AgentService.on(AgentServiceEvent.ACCOUNT_CREATED, this._createdAccountCallback);

        if ( !this.props.hasSession && !FormLoginModal.isOpen()) {
            FormLoginModal.openModal();
        }

    }

    public componentWillUnmount (): void {

        if (this._saveTimeout !== undefined) {
            clearTimeout(this._saveTimeout);
            this._saveTimeout = undefined;
            LOG.debug(`componentWillUnmount: Saving now.`);
            this._saveBackendItem();
        }

        if (this._createdListener) {
            this._createdListener();
            this._createdListener = undefined;
        }

    }

    public componentDidUpdate (prevProps: Readonly<AgentViewProps>, prevState: Readonly<AgentViewState>, snapshot?: any): void {

        if (this.props.hasSession && !prevProps.hasSession && this.state.componentState !== AgentViewComponentState.LOADING) {
            this._loadBackendItem();
        }

    }

    public render () {

        const model          = this.state?.model;
        // const dto            = this.state?.dto;
        const modelVersion   = this.state.version;
        const modelHistory   = this.state.modelHistory;
        const componentState = this.state.componentState;
        const isLoading      = componentState === AgentViewComponentState.LOADING;
        const isSaving       = componentState === AgentViewComponentState.SAVING;

        const accountList    : RepositoryMember[] = map(this.state?.dto?.accounts ?? [], (item : AgentAccountDTO) : RepositoryMember => {
            return {
                id: item?.model?.username ?? item?.id ?? '',
                displayName: item?.model?.displayName
            };
        });

        // FIXME: Implement error state handling

        return (
            <div className={FormAppClassName.AGENT_VIEW}>

                <header className={FormAppClassName.AGENT_VIEW + '-header'}>

                    <h3 className={FormAppClassName.AGENT_VIEW + '-header-title'}>Agent {model?.name} v{modelVersion}</h3>

                    <div className={FormAppClassName.AGENT_VIEW + '-header-main-buttons'}>

                        {this.props.hasSession ? (

                            <React.Fragment>

                                <Button
                                    click={this._addNewCallback}
                                    className={FormAppClassName.AGENT_VIEW + '-header-add-button'}
                                ><Icon><AddIcon /></Icon> Create agent</Button>

                                {this.state.account ? (

                                    <TextField
                                        label={'Access Token'}
                                        value={this.state.account.access_token}
                                    />

                                ) : null}

                            </React.Fragment>

                        ) : null}

                        {modelHistory.length ? (
                            <Button click={this._undoModelCallback}><Icon><UndoIcon /></Icon></Button>
                        ): null}

                        {isSaving ? (
                            <Loader className={FormAppClassName.AGENT_VIEW + '-editor-loader'} />
                        ) : null}

                    </div>

                    <div className={FormAppClassName.AGENT_VIEW + '-header-side-buttons'}>

                        {this.props.hasSession ? (
                            <Button
                                className={FormAppClassName.AGENT_VIEW + '-delete-button'}
                                click={this._deleteCallback}><Icon><TrashIcon /></Icon> Delete pool</Button>
                        ) : null}

                    </div>

                </header>

                <div className={FormAppClassName.AGENT_VIEW + '-editor'}>

                    {this.props.hasSession ? (

                        <React.Fragment>

                            <TextField
                                className={FormAppClassName.AGENT_VIEW + '-editor-field-name'}
                                label={'Name'}
                                value={this.state.model?.name ?? ''}
                                change={this._changeModelNameCallback}
                            />

                            <MemberList
                                className={FormAppClassName.AGENT_VIEW + '-editor-list'}
                                list={accountList}
                            />

                        </React.Fragment>

                    ) : (
                        <div>
                            <strong>You need to log in.</strong>
                        </div>
                    )}

                </div>

                {isLoading ? (
                    <Loader className={FormAppClassName.AGENT_VIEW + '-loader'} />
                ) : null}

            </div>
        );

    }


    private _setModel (model: AgentPoolModel) {

        const origModel        = this.state?.model;
        const origModelHistory = this.state.modelHistory;

        let modelHistory : AgentPoolModel[] = origModel ? [
            origModel,
            ...origModelHistory
        ] : origModelHistory;

        if (modelHistory.length >= UNDO_HISTORY_LENGTH) {
            modelHistory = modelHistory.slice(0, UNDO_HISTORY_LENGTH);
        }

        this.setState({
            model: model,
            modelHistory: modelHistory
        });

        this._updateBackend();

    }

    private _onChangeModel (model: AgentPoolModel) {
        this._setModel(model);
    }

    private _onUndoModel () {

        const [
            prevModel,
            ...modelHistory
        ] = this.state.modelHistory;

        this.setState({
            model: prevModel,
            modelHistory: modelHistory
        });

        this._updateBackend();

    }

    private _loadBackendItem () {

        if ( this._componentState === AgentViewComponentState.LOADING
            || this._componentState === AgentViewComponentState.SAVING
        ) {
            LOG.warn(`_loadBackendItem: Already loading or saving`);
            return;
        }

        LOG.debug(`_loadBackendItem: Loading now.`);

        this._componentState = AgentViewComponentState.LOADING;

        this.setState({componentState: this._componentState});

        AgentService.getAgentPool(this.props.id).then((item : RepositoryEntry<AgentPoolDTO>) => {

            this._componentState = AgentViewComponentState.LOADED;

            this.setState({
                componentState : this._componentState,
                id             : item.id,
                version        : item.version,
                model          : item.data.model,
                dto            : item.data
            });

        }).catch(err => {

            this._componentState = AgentViewComponentState.LOAD_ERROR;

            LOG.error(`_loadBackendItem: Loading failed: `, err);

            // FIXME: Display error

            this.setState({
                componentState: this._componentState
            });

        });

    }

    private _saveBackendItem () {

        if ( this._componentState === AgentViewComponentState.LOADING
            || this._componentState === AgentViewComponentState.SAVING
        ) {
            LOG.warn(`_saveForm: Already loading or saving form`);
            return;
        }

        LOG.debug(`_saveForm: Saving form now.`);

        this._componentState = AgentViewComponentState.SAVING;

        this.setState({componentState: this._componentState});

        const model = this.state?.model;

        if (!model) {
            LOG.error(`_saveBackendItem: Model not defined.`);
            return;
        }

        AgentService.saveAgentPool(
            this.props.id,
            model
        ).then((form : RepositoryEntry<AgentPoolDTO>) => {

            this._componentState = AgentViewComponentState.LOADED;

            if (this._saveTimeout !== undefined) {

                LOG.info(`New save is pending.`);

            } else {

                LOG.info(`Save finished.`);

                this.setState({
                    componentState : this._componentState,
                    id             : form.id,
                    version        : form.version,
                    model          : form.data.model
                });

            }

        }).catch(err => {

            this._componentState = AgentViewComponentState.SAVE_ERROR;

            LOG.error(`_saveBackendItem: Failed to save: `, err);

            // FIXME: Display error

            this.setState({
                componentState: this._componentState
            });

        });

    }

    private _updateBackend () {

        if (this._saveTimeout !== undefined) {
            clearTimeout(this._saveTimeout);
            this._saveTimeout = undefined;
        }

        LOG.debug(`_updateBackend: Triggered save later.`);

        this._saveTimeout = setTimeout( () => {

            this._saveTimeout = undefined;

            if ( this._componentState === AgentViewComponentState.LOADING
                || this._componentState === AgentViewComponentState.SAVING ) {
                LOG.debug(`_updateBackend: Already loading or saving form`);
                this._updateBackend();
                return;
            }

            LOG.debug(`_updateBackend: Saving form now.`);
            this._saveBackendItem();

        }, AGENT_SAVE_TIMEOUT)

    }

    private _onDelete () {

        // FIXME: Implement a spinner
        // FIXME: Implement a confirmation or undo modal

        const id = this.props.id;
        AgentService.deleteAgentPool(id).then(() => {

            LOG.info(`Removed ${id}`);

            RouteService.setRoute(AppRoute.AGENT_LIST_VIEW);

        }).catch(err => {

            // FIXME: Display UI error
            LOG.error(`Could not delete ${id}: `, err);

        });

    }

    private _onCreated (
        event   : AgentServiceEvent,
        account : AgentAccountDTO
    ) {

        // FIXME: Move this to a modal and add info about saving the access token

        LOG.debug(`account : `, account);
        this.setState({account});

        this._loadBackendItem();

    }

    private _addNew () {

        if (!this.props.hasSession) {
            LOG.error(`_addNew: We did not have session!`)
            return
        }

        AgentService.createAgentAccount(this.props.id);

    }

}

export default AgentView;
